import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useApolloClient, useQuery } from '@apollo/client'
import { useForm } from 'react-hook-form'

import { mutations, queries } from '../../graphql'
import {
  Box,
  Button,
  CancelButton,
  CardBox,
  Container,
  DataGrid,
  EditButton,
  Icons,
  Link,
  Row,
  Stack,
  Tab,
  Tabs,
  Text,
  useAlert,
} from '../../components'
import {
  formatContributionDescription,
  titleCase,
  toAmountString,
  toDateStringNumeric,
  toDateStringInTimezone,
  userName,
} from '../../utils'
import { useTimezone } from '../../hooks'
import * as Card from '../../components/payment/credit-card'
import * as Bank from '../../components/payment/bank-transfer'
import { PAYMENT_TYPE } from '../../constants'
import { Filters } from './filters'

export function Recurrent() {
  const timezone = useTimezone()
  const [showRContributions, setShowRContributions] = useState(true)
  const [showRGrantRequests, setShowRGrantRequests] = useState(true)
  const [showRDafTransfers, setShowRDafTransfers] = useState(true)
  // const [edit, setEdit] = useState(null)
  const { fundId } = useParams()
  const { loading, data: { me } = {} } = useQuery(queries.funds.myRecurring, {
    variables: { fundId: Number(fundId) },
  })

  const gridRef = useRef()
  const gridApi = useRef()

  const [selection, setSelection] = useState([])
  const selectSingleRow = (id) => gridApi.current.selectRows([id], /* isSelected */ true, /* resetSelection */ true)
  const clearSelection = () => gridApi.current.selectRows([], /* isSelected */ true, /* resetSelection */ true)
  const edit = useMemo(
    () => (selection.length ? me?.fund?.recurrentContributions?.find((c) => `c${c.id}` === selection[0]) : null),
    [selection, me]
  )

  function getScheduledDate(createdDate, nextScheduledDate, dayInMonth, monthInYear) {
    const createdAt = new Date(createdDate)
    const nextScheduled = new Date(nextScheduledDate)

    let year = createdAt.getFullYear()

    if (monthInYear < createdAt.getMonth() + 1) {
      year += 1
    }

    return new Date(
      year,
      monthInYear - 1,
      dayInMonth,
      nextScheduled.getHours(),
      nextScheduled.getMinutes(),
      nextScheduled.getSeconds(),
      nextScheduled.getMilliseconds()
    )
  }

  function getNextPaymentDate(scheduledDate, nextDate) {
    const now = new Date()

    if (scheduledDate > now) {
      return scheduledDate
    }

    return new Date(nextDate)
  }

  const rContributions = showRContributions
    ? me?.fund?.recurrentContributions
        .filter((c) => c.state === 'active')
        .map((rc) => ({
          ...rc,
          id: `c${rc.id}`,
          recordId: rc.id,
          nextPaymentDate: getNextPaymentDate(
            getScheduledDate(rc.createdAt, rc.nextScheduledCharge, rc.dayInMonth, rc.monthInYear),
            rc.nextScheduledCharge
          ),
          nextPaymentDateTable: getNextPaymentDate(
            getScheduledDate(rc.createdAt, rc.nextScheduledCharge, rc.dayInMonth, rc.monthInYear),
            rc.nextScheduledCharge
          ),
          createdAtTable: rc.createdAt,
          description: formatContributionDescription(rc),
          descriptionElement: (
            <>
              <Text.AltBody>{formatContributionDescription(rc)}</Text.AltBody>
              <Box sx={{ display: 'flex', flexGrow: 1 }}>
                <Button
                  variant="text"
                  sx={{ color: 'primary.dark', visibility: 'hidden' }}
                  onClick={() => selectSingleRow(`c${rc.id}`)}
                >
                  <Icons.EditPayment />
                </Button>
              </Box>
            </>
          ),
          grossAmount: rc.amount,
        })) || []
    : []
  const rGrantRequests = showRGrantRequests
    ? me?.fund?.recurrentGrantRequests
        .filter((g) => g.state === 'active')
        .map((rgr) => {
          const description = [rgr?.charity, rgr?.grantNote]

          return {
            ...rgr,
            id: `g${rgr.id}`,
            recordId: rgr.id,
            nextPaymentDate: getNextPaymentDate(
              getScheduledDate(rgr.createdAt, rgr.nextScheduledGrant, rgr.dayInMonth, rgr.monthInYear),
              rgr.nextScheduledGrant
            ),
            nextPaymentDateTable: getNextPaymentDate(
              getScheduledDate(rgr.createdAt, rgr.nextScheduledGrant, rgr.dayInMonth, rgr.monthInYear),
              rgr.nextScheduledGrant
            ),
            createdAtTable: rgr.createdAt,
            isGrantRequest: true,
            description,
            descriptionElement: (
              <>
                <Text.AltBody>
                  {rgr.charity && (
                    <Link to={`/funds/${fundId}/grant/${rgr.charity?.id}`}>{rgr.charity?.accountName}</Link>
                  )}
                </Text.AltBody>
                <Box sx={{ display: 'flex', flexGrow: 1 }}>
                  <Button
                    variant="text"
                    sx={{ color: 'primary.dark', visibility: 'hidden' }}
                    onClick={() => selectSingleRow(`c${rgr.id}`)}
                  >
                    <Icons.EditPayment />
                  </Button>
                </Box>
              </>
            ),
            grossAmount: rgr.amount,
          }
        }) || []
    : []
  const rDafTransfers = showRDafTransfers
    ? [
        ...(me?.fund?.recurrentDafTransfers || []).map((rdt) => ({ ...rdt, isOutgoing: true })),
        ...(me?.fund?.isGenerosityFund
          ? (me?.fund?.recurrentDafTransfersIncoming || []).map((rdt) => ({ ...rdt, isOutgoing: false }))
          : []),
      ]
        .filter((c) => c.state === 'active')
        .map((rdt) => {
          const receivingFundDonorName = () =>
            userName(null, rdt?.receivingFund?.fundProfile) || rdt?.receivingFund?.name || ''
          const initiatingUserDonorName = () =>
            userName(rdt?.initiatingUser?.profile, rdt?.initiatingFund?.fundProfile, '', rdt?.initiatingUser) ||
            'Unknown'

          const getDescription = () => {
            if (rdt.isOutgoing) {
              return `To ${receivingFundDonorName()}`
            }
            return `From ${initiatingUserDonorName()}`
          }

          const getDescriptionElement = () => {
            if (rdt.isOutgoing) {
              return rdt?.receivingFund ? (
                <>
                  <span>To: </span>
                  <Link to={`/funds/${fundId}/share-funds-with-generosity-fund/${rdt?.receivingFund?.id}`}>
                    {receivingFundDonorName()}
                  </Link>
                </>
              ) : null
            }

            // Incoming transfer
            return rdt?.initiatingUser ? (
              <>
                <span>From </span>
                <span>{initiatingUserDonorName()}</span>
              </>
            ) : (
              <span>From Unknown</span>
            )
          }

          return {
            ...rdt,
            id: `daf${rdt.id}`,
            recordId: rdt.id,
            nextPaymentDate: getNextPaymentDate(
              getScheduledDate(rdt.createdAt, rdt.nextScheduledTransfer, rdt.dayInMonth, rdt.monthInYear),
              rdt.nextScheduledTransfer
            ),
            nextPaymentDateTable: getNextPaymentDate(
              getScheduledDate(rdt.createdAt, rdt.nextScheduledTransfer, rdt.dayInMonth, rdt.monthInYear),
              rdt.nextScheduledTransfer
            ),
            createdAtTable: rdt.createdAt,
            description: getDescription(),
            grossAmount: rdt.amount,
            descriptionElement: (
              <>
                <Text.AltBody>{getDescriptionElement()}</Text.AltBody>
                {/* This button below is hidden only to maintain the vertical
            spacing consistency with other rows in the table */}
                <Box sx={{ display: 'flex', flexGrow: 1 }}>
                  <Button
                    variant="text"
                    sx={{ color: 'primary.dark', visibility: 'hidden' }}
                    onClick={() => selectSingleRow(`c${rdt.id}`)}
                  >
                    <Icons.EditPayment />
                  </Button>
                </Box>
              </>
            ),
          }
        }) || []
    : []

  const rows = [...rContributions, ...rGrantRequests, ...rDafTransfers]
  const columns = [
    {
      field: '__typename',
      headerName: 'Transaction',
      flex: 0.5,
      align: 'center',
      minWidth: 44,
      maxWidth: 44,
      renderCell: ({ row } = {}) => <Icons.ActivityItem row={row} />,
      renderHeader: () => null,
      showInExpandedRowPanel: false,
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      flex: 1,
      minWidth: 100,
      type: 'date',
      hide: true,
      valueFormatter: ({ value } = {}) => toDateStringNumeric(value),
    },
    {
      field: 'createdAtTable',
      headerName: 'Created',
      flex: 1,
      minWidth: 140,
      type: 'date',
      exportable: false,
      valueFormatter: ({ value }) => toDateStringInTimezone(value, timezone),
    },
    {
      field: 'nextPaymentDate',
      headerName: 'Next Date',
      flex: 1,
      minWidth: 140,
      type: 'date',
      hide: true,
      valueFormatter: ({ value }) => toDateStringNumeric(value),
    },
    {
      field: 'nextPaymentDateTable',
      headerName: 'Next Date',
      flex: 1,
      minWidth: 160,
      type: 'date',
      exportable: false,
      valueFormatter: ({ value }) => toDateStringInTimezone(value, timezone),
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      flex: 1,
      minWidth: 100,
      valueFormatter: ({ value } = {}) => titleCase(value),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 300,
      sortable: false,
      renderCell: ({ row }) => row.descriptionElement,
      valueGetter: ({ row }) => {
        if (!row) return ''

        const { __typename: type } = row
        if (type === 'RecurrentDafTransfer') {
          return row.description
        }

        if (row.isGrantRequest && Array.isArray(row.description)) {
          const [charity] = row.description
          return charity?.accountName ? `${charity.accountName} (${charity.id})` : ''
        }

        return row.description || ''
      },
    },
    {
      field: 'purposeOfGift',
      headerName: 'Gift Designation',
      flex: 1,
      minWidth: 200,
      hide: true,
      valueGetter: ({ row }) => row?.grantNote || '',
    },
    {
      field: 'amount',
      headerName: 'Amount',
      pinnable: true,
      hideable: true,
      flex: 1,
      minWidth: 105,
      exportable: false,
      valueFormatter: ({ value } = {}) => toAmountString(value),
    },
    {
      field: 'grossAmount',
      headerName: 'Gross Amount',
      flex: 1,
      minWidth: 80,
      hide: true,
      valueFormatter: ({ value } = {}) => toAmountString(value),
    },
    {
      field: 'tip',
      headerName: 'Courtesy Fee',
      pinnable: true,
      hideable: true,
      hide: true,
      flex: 1,
      valueFormatter: ({ value } = {}) => toAmountString(value),
    },
    {
      field: 'charityName',
      headerName: 'Charity Name',
      flex: 1,
      minWidth: 200,
      hide: true,
      exportable: false,
      valueGetter: ({ row }) => row?.charity?.accountName || row?.receivingFund?.name || '',
    },
    {
      field: 'registeredCharityNumber',
      headerName: 'Registered Charity Number',
      flex: 1,
      minWidth: 200,
      hide: true,
      exportable: false,
      valueGetter: ({ row }) => row?.charity?.id || '',
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 1,
      minWidth: 150,
      hide: true,
      renderCell: ({ value } = {}) => <Icons.State state={value} />,
    },
    {
      field: 'edit',
      headerName: 'Edit',
      flex: 1,
      hideable: false,
      sortable: false,
      exportable: false,
      maxWidth: 80,
      renderCell: ({
        id,
        row: {
          __typename,
          recordId,
          state,
          amount,
          tip,
          frequency,
          nextScheduledGrant,
          nextScheduledTransfer,
          nextScheduledCharge,
          grantNote,
          anonymous,
          isOutgoing,
          paymentMethod,
          usedGiveGenius,
          linkedRecurrentGrantRequestAmount,
          linkedRecurrentGrantRequestTip,
          linkedRecurrentContributionAmount,
        },
      }) => {
        if (__typename === 'RecurrentContribution') {
          return state === 'active' ? (
            <EditButton
              type="Contribution"
              mutation={mutations.contributions.userUpdateRecurrentContribution}
              refetchQueries={['MyRecurring']}
              usedGiveGenius={usedGiveGenius}
              linkedAmount={linkedRecurrentGrantRequestAmount}
              linkedTip={linkedRecurrentGrantRequestTip}
              variables={{
                id: recordId,
                fundId: Number(fundId),
                amount,
                nextScheduledCharge,
                frequency,
                paymentMethodId: paymentMethod?.id,
                paymentMethod,
              }}
            />
          ) : null
        }
        if (__typename === 'RecurrentGrantRequest') {
          return state === 'active' ? (
            <EditButton
              type="Gift"
              mutation={mutations.grantRequests.userUpdateRecurrentGrantRequest}
              refetchQueries={['MyRecurring']}
              usedGiveGenius={usedGiveGenius}
              linkedAmount={linkedRecurrentContributionAmount}
              variables={{
                id: recordId,
                fundId: Number(fundId),
                amount,
                tip,
                nextScheduledGrant,
                frequency,
                grantNote,
                anonymous,
              }}
            />
          ) : null
        }
        if (__typename === 'RecurrentDafTransfer') {
          return state === 'active' && isOutgoing ? (
            <EditButton
              type="Shared Fund"
              mutation={mutations.dafTransfers.userUpdateRecurrentDafTransfer}
              refetchQueries={['MyRecurring']}
              variables={{ id: recordId, fundId: Number(fundId), amount, nextScheduledTransfer, frequency }}
            />
          ) : null
        }
        return null
      },
    },
    {
      field: 'cancel',
      headerName: 'Cancel',
      flex: 1,
      hideable: false,
      sortable: false,
      exportable: false,
      maxWidth: 80,
      renderCell: ({
        id,
        row: {
          __typename,
          recordId,
          state,
          amount,
          frequency,
          isOutgoing,
          usedGiveGenius,
          linkedRecurrentGrantRequestAmount,
          linkedRecurrentGrantRequestFrequency,
          linkedRecurrentGrantRequestCharityName,
          linkedRecurrentContributionAmount,
          linkedRecurrentContributionFrequency,
        },
      }) => {
        if (__typename === 'RecurrentContribution') {
          return state === 'active' ? (
            <CancelButton
              id={id}
              title="Confirm"
              details={
                <Text.AltBody>
                  Recurring Contribution
                  <br />
                  {toAmountString(amount)}
                  <br />
                  {frequency}
                  {usedGiveGenius && (
                    <Box sx={{ mt: 1 }}>
                      This will also cancel the linked recurring gift:
                      <br />
                      <b>
                        {toAmountString(linkedRecurrentGrantRequestAmount)} {linkedRecurrentGrantRequestFrequency} to{' '}
                        {linkedRecurrentGrantRequestCharityName}
                      </b>
                    </Box>
                  )}
                </Text.AltBody>
              }
              mutation={mutations.contributions.cancelRecurringContribution}
              refetchQueries={['MyFund', 'MyLinkedBanks', 'MyCreditCards', 'linkedZumRailsBanks', 'MyRecurring']}
              variables={{ id: recordId, fundId: Number(fundId) }}
            />
          ) : null
        }
        if (__typename === 'RecurrentGrantRequest') {
          return state === 'active' ? (
            <CancelButton
              id={id}
              title="Confirm"
              details={
                <Text.AltBody>
                  Recurring Gift Request
                  <br />
                  {toAmountString(amount)}
                  <br />
                  {frequency}
                  {usedGiveGenius && (
                    <Box sx={{ mt: 1 }}>
                      This will also cancel the linked recurring contribution:
                      <br />
                      <b>
                        {toAmountString(linkedRecurrentContributionAmount)} {linkedRecurrentContributionFrequency}
                      </b>
                    </Box>
                  )}
                </Text.AltBody>
              }
              mutation={mutations.grantRequests.cancelRecurringGrantRequest}
              refetchQueries={['MyFund', 'MyRecurring']}
              variables={{ id: recordId, fundId: Number(fundId) }}
            />
          ) : null
        }
        if (__typename === 'RecurrentDafTransfer') {
          return state === 'active' && isOutgoing ? (
            <CancelButton
              id={id}
              title="Confirm"
              details={
                <Text.AltBody>
                  Recurring Transfer
                  <br />
                  {toAmountString(amount)}
                  <br />
                  {frequency}
                </Text.AltBody>
              }
              mutation={mutations.dafTransfers.cancelRecurrentDafTransfer}
              refetchQueries={['MyFund']}
              variables={{ id: recordId, fundId: Number(fundId) }}
            />
          ) : null
        }
        return null
      },
    },
  ]

  return (
    <>
      <Container maxWidth="lg">
        <CardBox>
          <DataGrid
            ref={gridRef}
            apiRef={gridApi}
            disableColumnSelector
            rows={rows}
            columns={columns}
            loading={loading}
            filters={
              <Filters
                showRGrantRequests={showRGrantRequests}
                setShowRGrantRequests={setShowRGrantRequests}
                showRContributions={showRContributions}
                setShowRContributions={setShowRContributions}
                showRDafTransfers={showRDafTransfers}
                setShowRDafTransfers={setShowRDafTransfers}
              />
            }
            initialState={{
              sorting: {
                sortModel: [{ field: 'createdAt', sort: 'desc' }],
              },
            }}
            autoRowHeight
            sx={{
              '& .MuiDataGrid-cell': {
                p: '3px',
              },
            }}
            localeText={{
              noRowsLabel: 'No recurring transactions found.',
            }}
            exportFileName={`Recurring Activity Table ${new Date().toISOString().split('T')[0]}`}
          />
        </CardBox>
      </Container>
      {edit && <UpdatePaymentMethod contribution={edit} close={clearSelection} />}
    </>
  )
}

function UpdatePaymentMethod({ contribution, close }) {
  const {
    id,
    fundId,
    paymentMethod: { paymentType },
  } = contribution
  const client = useApolloClient()
  const cardApi = useRef()
  const [{ Alert, alertProps }, { setAlert }] = useAlert()
  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({ mode: 'onSubmit', defaultValues: { paymentMethodId: contribution.paymentMethod.id } })

  /* either credit-card or bank-transfer */
  const [tab, setTab] = useState('credit-card')

  /* when the user switches the selected contribution, reset the form with new default */
  useEffect(() => reset({ paymentMethodId: contribution.paymentMethod.id }), [reset, contribution.paymentMethod.id])
  /* when the current payment type changes, switch tabs in order to show the current payment method */
  useEffect(() => setTab(paymentType === PAYMENT_TYPE.bankTransfer ? 'bank-transfer' : 'credit-card'), [paymentType])

  const onSubmit = async ({ multiUse, paymentMethodId: formPaymentMethodId }) => {
    let newPayment = Number(formPaymentMethodId)

    if (tab === 'credit-card' && cardApi.current.isNewCard) {
      try {
        newPayment = await cardApi.current.postToStripeAndSavePaymentMethod({ multiUse })
      } catch (e) {
        setAlert({
          message: `Something went wrong using this card`,
          error: e.message || e.type,
          severity: 'error',
        })
        return
      }
    }

    await client.mutate({
      mutation: mutations.contributions.updateRecurrentContributionPaymentMethod,
      variables: { id, fundId, paymentMethodId: newPayment },
      refetchQueries: [{ query: queries.funds.myRecurring, variables: { fundId } }],
      awaitRefetchQueries: true,
    })

    close()
  }

  return (
    <Container maxWidth="lg">
      <Text.H6>Update Payment Method</Text.H6>
      <Tabs value={tab} onChange={(_, v) => setTab(v)}>
        <Tab label="Credit Card" value="credit-card" />
        <Tab label="Bank Transfer" value="bank-transfer" />
      </Tabs>
      <CardBox>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack>
            {tab === 'credit-card' ? (
              <>
                <Card.Select ref={cardApi} register={register} fundId={fundId} />
                <Card.FeesNotice />
              </>
            ) : (
              <>
                <Bank.FAQ />
                <Bank.Select register={register} fundId={fundId} />
              </>
            )}
            <Row sx={{ display: 'flex', justifyContent: 'end' }}>
              <Button disabled={isSubmitting} onClick={close} color="info">
                Back
              </Button>
              <Button disabled={isSubmitting} type="submit">
                Confirm
              </Button>
            </Row>
            <Alert {...alertProps} sx={{ alignSelf: 'flex-end' }} />
          </Stack>
        </form>
      </CardBox>
    </Container>
  )
}
